import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DeckAdminService, DeckGroup } from '../../deck-admin.service';
import { AgentMultiSelectComponent } from '../agent-multi-select/agent-multi-select.component';
import { DropdownComponent, DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { CountryCurrencyData, COUNTRY_CURRENCY_LIST } from '../../../../../shared/utils/country-currency-options.util';

export type CategoryPanelMode = 'create' | 'edit';

/**
 * Create or edit one deck CATEGORY.
 *
 * The category — not its pages — carries `when_to_show`: the situation that should make
 * the AI open it. It also carries the two fields that decide which slide opens a call:
 * `is_intro` (the default opener, at most one per deck) and `industry` (a canonical key,
 * so an orthodontist opens on the dental category). Both are set here, deliberately,
 * instead of being guessed in the browser at call time.
 */
@Component({
  selector: 'app-category-form-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, AgentMultiSelectComponent, DropdownComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="fixed inset-0 z-[8000] bg-black/50 backdrop-blur-sm" (click)="onClose()"></div>

    <div
      class="fixed top-0 right-0 z-[8001] h-dvh w-full max-w-[520px] bg-white dark:bg-surface-1 shadow-2xl flex flex-col"
      (click)="$event.stopPropagation()"
      id="category_form_panel"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-5 border-b border-slate-100 dark:border-slate-700/60 shrink-0">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm">
            <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            </svg>
          </div>
          <div>
            <h2 class="text-sm font-semibold text-slate-800 dark:text-white">
              {{ mode === 'create' ? 'New Category' : 'Edit Category' }}
            </h2>
            <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
              {{ mode === 'create' ? 'A group of slides the AI opens as one unit' : 'Metadata only — pages are managed separately' }}
            </p>
          </div>
        </div>
        <button
          (click)="onClose()"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
          id="category_panel_close_btn"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        <div
          *ngIf="errorMessage()"
          class="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-xs text-red-700 dark:text-red-400"
          id="category_panel_error"
        >
          {{ errorMessage() }}
        </div>

        <!-- group_key -->
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
            Category key <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            [(ngModel)]="form.group_key"
            [disabled]="mode === 'edit'"
            placeholder="spa_overview"
            class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                   text-sm text-slate-800 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600
                   focus:outline-none focus:ring-2 focus:ring-purple-500/40 disabled:opacity-60 font-mono"
            id="category_panel_group_key"
          />
          <p class="text-[10px] text-slate-400 mt-1">
            Lowercase letters, digits, underscores. This is the name the AI calls with
            <code class="font-mono">show_deck</code>. It cannot be changed later.
          </p>
        </div>

        <!-- title -->
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
            Title <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            [(ngModel)]="form.title"
            placeholder="Spa Overview"
            class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                   text-sm text-slate-800 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600
                   focus:outline-none focus:ring-2 focus:ring-purple-500/40"
            id="category_panel_title"
          />
        </div>

        <!-- when_to_show -->
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
            When should the AI open this? <span class="text-red-500">*</span>
          </label>
          <textarea
            [(ngModel)]="form.when_to_show"
            rows="3"
            placeholder="Early in the call with a med spa, once they've described their treatment mix."
            class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                   text-sm text-slate-800 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600
                   focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-none"
            id="category_panel_when"
          ></textarea>
          <p class="text-[10px] text-slate-400 mt-1">
            The AI reads this to decide which category fits the moment. Individual slides
            do not have their own “when” — the category owns it.
          </p>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <!-- group_index -->
          <div>
            <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
              Order <span class="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              [(ngModel)]="form.group_index"
              class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                     text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              id="category_panel_index"
            />
          </div>

          <!-- industry -->
          <div>
            <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
              Industry
            </label>
            <select
              [(ngModel)]="form.industry"
              class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                     text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              id="category_panel_industry"
            >
              <option value="">Generic (any industry)</option>
              <option *ngFor="let key of industries" [value]="key">{{ key }}</option>
            </select>
          </div>
        </div>

        <!-- country (multi-select) -->
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
            Countries
          </label>
          <app-dropdown
            [options]="countryDropdownOptions"
            [selectedValue]="form.country"
            (selectionChange)="onCountrySelectionChange($event)"
            selectionMode="multiple"
            selectionStyle="checkbox"
            placeholder="All Countries (Global)"
            [searchable]="true"
            searchPlaceholder="Search countries…"
            [clearable]="true"
            size="md"
            variant="themeBase"
            triggerClass="!min-h-[42px] !rounded-xl !border-slate-200 dark:!border-slate-700 bg-white dark:bg-slate-800/50"
            rounded="xl"
            id="category_panel_country"
          ></app-dropdown>
          <p class="text-[10px] text-slate-400 mt-1">
            <strong>All Countries (Global)</strong> if none selected. Select one or more countries to restrict this category to calls from those regions.
          </p>
        </div>

        <!-- agent assignments -->
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
            Agents
          </label>
          <app-agent-multi-select
            [selected]="form.agent_ids"
            (selectedChange)="form.agent_ids = $event"
          ></app-agent-multi-select>
          <p class="text-[10px] text-slate-400 mt-1">
            The agent prompts this category belongs to. A category with no agent selected is
            assigned to <strong>no agent</strong>.
          </p>
        </div>

        <!-- is_intro -->
        <label
          class="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer
                 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
        >
          <input
            type="checkbox"
            [(ngModel)]="form.is_intro"
            class="mt-0.5 w-4 h-4 rounded accent-purple-600"
            id="category_panel_is_intro"
          />
          <span>
            <span class="block text-xs font-semibold text-slate-700 dark:text-slate-200">Opens the call</span>
            <span class="block text-[10px] text-slate-400 mt-0.5">
              The default category when no industry matches. Only one category can hold this —
              turning it on here turns it off wherever it was.
            </span>
          </span>
        </label>

        <!-- is_active -->
        <label
          class="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer
                 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
        >
          <input
            type="checkbox"
            [(ngModel)]="form.is_active"
            class="mt-0.5 w-4 h-4 rounded accent-emerald-600"
            id="category_panel_is_active"
          />
          <span>
            <span class="block text-xs font-semibold text-slate-700 dark:text-slate-200">Active</span>
            <span class="block text-[10px] text-slate-400 mt-0.5">
              Inactive categories are hidden from the AI but kept here.
            </span>
          </span>
        </label>
      </div>

      <!-- Footer -->
      <div class="flex gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-700/60 shrink-0">
        <button
          (click)="onClose()"
          class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
          id="category_panel_cancel_btn"
        >
          Cancel
        </button>
        <button
          (click)="onSubmit()"
          [disabled]="submitting()"
          class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-600
                 hover:from-purple-600 hover:to-indigo-700 disabled:opacity-60 transition-all active:scale-95"
          id="category_panel_submit_btn"
        >
          <span *ngIf="!submitting()">{{ mode === 'create' ? 'Create Category' : 'Save Changes' }}</span>
          <span *ngIf="submitting()" class="flex items-center justify-center gap-2">
            <span class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
            Saving…
          </span>
        </button>
      </div>
    </div>
  `,
})
export class CategoryFormPanelComponent implements OnChanges {
  @Input() mode: CategoryPanelMode = 'create';
  @Input() group: DeckGroup | null = null;
  /** Canonical industry keys, fetched once by the parent. */
  @Input() industries: string[] = [];
  /** Country options strictly sourced from country-currency-options.util.ts */
  readonly countryDropdownOptions: DropdownOption[] = COUNTRY_CURRENCY_LIST.map(
    (c: CountryCurrencyData) => ({
      label: c.country_name,
      value: c.country_name,
      shortLabel: c.country_code,
      image: c.country_flag,
    })
  );
  /** Suggested order for a new category (parent passes groups.length + 1). */
  @Input() nextIndex = 1;

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private deckService = inject(DeckAdminService);

  submitting = signal(false);
  errorMessage = signal('');

  form = {
    group_key: '',
    title: '',
    when_to_show: '',
    group_index: 1,
    industry: '' as string,
    country: [] as string[],
    is_intro: false,
    is_active: true,
    agent_ids: [] as number[],
  };

  onCountrySelectionChange(val: any): void {
    if (Array.isArray(val)) {
      this.form.country = val.map(String).filter(Boolean);
    } else if (typeof val === 'string' && val) {
      this.form.country = [val];
    } else {
      this.form.country = [];
    }
  }

  private parseInitCountry(val: unknown): string[] {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(String).filter(Boolean);
    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return [];
      if (s.startsWith('[')) {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
        } catch {}
      }
      return [s];
    }
    return [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] || changes['mode']) {
      this.errorMessage.set('');
      if (this.mode === 'edit' && this.group) {
        this.form = {
          group_key: this.group.group,
          title: this.group.title ?? '',
          when_to_show: this.group.when ?? '',
          group_index: this.group.index ?? 1,
          industry: this.group.industry ?? '',
          country: this.parseInitCountry(this.group.country),
          is_intro: this.group.isIntro ?? false,
          is_active: this.group.isActive ?? true,
          agent_ids: (this.group.agents ?? []).map((agent) => agent.id),
        };
      } else {
        this.form = {
          group_key: '',
          title: '',
          when_to_show: '',
          group_index: this.nextIndex,
          industry: '',
          country: [],
          is_intro: false,
          is_active: true,
          agent_ids: [],
        };
      }
    }
  }

  onSubmit(): void {
    this.errorMessage.set('');
    if (!this.form.group_key.trim()) return this.errorMessage.set('Category key is required.');
    if (!this.form.title.trim()) return this.errorMessage.set('Title is required.');
    if (!this.form.when_to_show.trim()) {
      return this.errorMessage.set('Tell the AI when to open this category.');
    }

    this.submitting.set(true);
    const done = {
      next: () => {
        this.submitting.set(false);
        this.saved.emit();
      },
      error: (err: any) => {
        this.submitting.set(false);
        this.errorMessage.set(err?.message ?? 'Save failed.');
      },
    };

    if (this.mode === 'create') {
      this.deckService
        .createGroup({
          group_key: this.form.group_key.trim(),
          title: this.form.title.trim(),
          when_to_show: this.form.when_to_show.trim(),
          group_index: Number(this.form.group_index) || 1,
          industry: this.form.industry || null,
          country: this.form.country.length ? this.form.country : null,
          is_intro: this.form.is_intro,
          is_active: this.form.is_active,
          agent_ids: this.form.agent_ids,
        })
        .subscribe(done);
      return;
    }

    // PATCH sends is_intro only when turning it ON: the backend has no "unset the
    // opener" operation, because a deck with no opener has nothing to show.
    // agent_ids is always sent — it REPLACES the assignment set ([] clears it).
    this.deckService
      .patchGroup(this.form.group_key, {
        title: this.form.title.trim(),
        when_to_show: this.form.when_to_show.trim(),
        group_index: Number(this.form.group_index) || 1,
        industry: this.form.industry || '',
        country: this.form.country.length ? this.form.country : null,
        is_active: this.form.is_active,
        agent_ids: this.form.agent_ids,
        ...(this.form.is_intro && !this.group?.isIntro ? { is_intro: true } : {}),
      })
      .subscribe(done);
  }

  onClose(): void {
    this.closed.emit();
  }
}
